import {
  documentToReactComponents,
  RenderNode,
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES } from '@contentful/rich-text-types';
import {
  isTypeButton,
  isTypeCalloutBox,
  isTypeDocumentLink,
  isTypeFaq,
  isTypePullQuote,
  isTypeWorldMap,
  isTypeYouTubeVideo,
} from '@wren/shared';
import { AssetFields } from 'contentful';
import { LinkedInEmbed, getSafeLinkedInEmbedUrl } from './LinkedInEmbed';
import { VideoEmbed } from './VideoEmbed';
import { HTMLAttributes, ReactElement } from 'react';
import { articleStyles, callout } from '../../style/article';
import { colors, spacing } from '../../style/theme';
import PullQuote from '../blog/PullQuote';
import { NavButton } from '../buttons/NavButton';
import DownloadableDocumentLink from '../projects/DownloadableDocumentLink';
import {
  ProjectLocation,
  ProjectLocationMap,
} from '../projects/ProjectLocationMap';
import { Expandable } from '../UI/Expandable';
import HorizontalSeparator from '../UI/HorizontalSeparator';

type Props = HTMLAttributes<HTMLDivElement> & {
  document: Document;
  renderNode?: RenderNode;
  withArticleStyle?: boolean;
};

const articleElementTypes: Record<string, string> = {
  blockquote: 'blockquote',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  li: 'li',
  ol: 'ol',
  p: 'p',
  table: 'table',
  td: 'td',
  th: 'th',
  tr: 'tr',
  ul: 'ul',
};

/***
 * We have a broad set of rules to be applied to blog articles: `articleStyles`,
 * but we want to avoid splitting article parts too much (affects selectors like 'p:last-of-type')
 * and we also don't want to apply any of those rules to embedded entities, because they affect common tags.
 * So, we wrap consecutive article elements into larger divs.
 ***/
function wrapArticleBlocks(body: ReactElement[]) {
  let articleChunk: ReactElement[] = [];
  const output: ReactElement[] = [];
  for (let index = 0; index < body.length; index++) {
    const node = body[index];
    if (
      node &&
      typeof node.type === 'string' &&
      articleElementTypes[node.type]
    ) {
      articleChunk.push(node);
      if (index === body.length - 1) {
        // end of body
        output.push(
          <div css={articleStyles} key={`article-chunk-${index}`}>
            {articleChunk}
          </div>
        );
      }
    } else {
      if (articleChunk.length > 0) {
        output.push(
          <div css={articleStyles} key={`article-chunk-${index}`}>
            {articleChunk}
          </div>
        );
        articleChunk = [];
      }
      output.push(node);
    }
  }
  return output;
}

function isExternal(href: string) {
  return /^http/.test(href) && !/^https?:\/\/([\w\d]+\.)?wren\.co/.test(href);
}

/** Styles for soft breaks (newlines) - only when inside a paragraph */
const softBreakInParagraphStyles = {
  'blockquote .soft-break': { display: 'block' as const, marginTop: '-16px' },
  '.soft-break': { display: 'block' as const, marginTop: '-6px' },
};

/** Renders text with newlines as soft breaks (smaller gap than a paragraph) */
function renderTextWithSoftBreaks(text: string) {
  const parts = text.split('\n');
  if (parts.length === 1) return text;
  return parts.reduce<React.ReactNode[]>((acc, part, i) => {
    if (i > 0) {
      acc.push(<span key={`break-${i}`} className="soft-break" aria-hidden />);
    }
    acc.push(part);
    return acc;
  }, []);
}

function mergeRenderNodes(a: RenderNode, b?: RenderNode): RenderNode {
  if (!b) return a;
  const merged: RenderNode = { ...b };
  for (const key in a) {
    if (b[key]) {
      merged[key] = (node, children) => {
        const element = b[key](node, children);
        if (element === undefined) {
          return a[key](node, children);
        }
        return element;
      };
    } else {
      merged[key] = a[key];
    }
  }
  return merged;
}

export default function RichText({
  withArticleStyle = true,
  document,
  renderNode,
  ...otherProps
}: Props) {
  const body = documentToReactComponents(document, {
    renderText: renderTextWithSoftBreaks,
    renderNode: mergeRenderNodes(
      {
        [INLINES.HYPERLINK]: ({ data }, children) => {
          return (
            <a
              href={data.uri}
              target={isExternal(data.uri) ? '_blank' : '_self'}
            >
              {children}
            </a>
          );
        },
        [BLOCKS.HEADING_2]: (node, children) => {
          const text = node.content
            .map(
              (content) =>
                content.nodeType === 'text' &&
                content.value
                  .replace(/\s+/g, '-')
                  .replace(/[^a-zA-Z0-9-]/g, '')
                  .toLowerCase()
            )
            .join('-');

          return <h2 id={text}>{children}</h2>;
        },
        [BLOCKS.HR]: () => (
          <HorizontalSeparator css={{ marginBlock: spacing.small }} />
        ),
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
          const fields = node.data.target.fields as AssetFields;
          return (
            <div
              css={{
                margin: `${spacing.large}px 0`,
                maxWidth: '100%',
              }}
            >
              <img
                alt={fields.description || ''}
                src={`${fields.file?.url}`}
                css={{
                  maxWidth: '100%',
                }}
              />
              {fields.description && (
                <figcaption
                  css={{
                    borderBottom: fields.description
                      ? `2px solid ${colors.borderColor}`
                      : '',
                    color: colors.textSecondary,
                    padding: spacing.small,
                  }}
                >
                  <i>{fields.description}</i>
                </figcaption>
              )}
            </div>
          );
        },
        [BLOCKS.EMBEDDED_ENTRY]: (node) => {
          const { target } = node.data;
          if (isTypeButton<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
            const { href, text, color } = target.fields;
            if (color === 'green')
              return (
                <a
                  css={{ textDecoration: 'none', marginTop: '24px' }}
                  href={href}
                >
                  <button>{text}</button>
                </a>
              );
            return (
              <div
                css={{
                  padding: spacing.medium,
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                <NavButton
                  buttonType="filled"
                  color={colors.wrenOrange}
                  href={href}
                >
                  {text}
                </NavButton>
              </div>
            );
          }

          if (isTypeWorldMap<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
            const locations = (target.fields.projectLocations ?? []).reduce<
              ProjectLocation[]
            >((acc, pin) => {
              const location = pin?.fields.location;
              if (location) {
                acc.push({
                  lat: location.lat,
                  lon: location.lon,
                  name: pin?.fields.name,
                });
              }
              return acc;
            }, []);
            return <ProjectLocationMap locations={locations} />;
          }

          if (
            isTypeDocumentLink<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)
          ) {
            return <DownloadableDocumentLink {...target.fields} />;
          }

          if (isTypeFaq<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
            return (
              <Expandable
                css={{ marginBottom: spacing.default }}
                heading={target.fields.heading}
              >
                <RichText
                  withArticleStyle={withArticleStyle}
                  document={target.fields.content}
                />
              </Expandable>
            );
          }

          if (isTypeCalloutBox<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
            return (
              <div
                css={[
                  callout,
                  { p: { lineHeight: 1.6 }, h2: { marginTop: 0 } },
                ]}
              >
                <RichText
                  document={target.fields.content}
                  withArticleStyle={false}
                />
              </div>
            );
          }

          if (isTypePullQuote<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)) {
            return <PullQuote {...target.fields} />;
          }

          if (
            isTypeYouTubeVideo<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>(target)
          ) {
            // A single entry carries a LinkedIn, Vimeo, or YouTube embed.
            // Precedence when more than one is set: LinkedIn, then Vimeo, then YouTube.
            const { pathAndQuery, vimeoPathAndQuery, linkedInUrl } =
              target.fields;
            // Only take the LinkedIn branch if the URL is an actual embed URL,
            // so a malformed LinkedIn value doesn't suppress a valid Vimeo or
            // YouTube fallback (which would render nothing).
            if (linkedInUrl && getSafeLinkedInEmbedUrl(linkedInUrl)) {
              return <LinkedInEmbed url={linkedInUrl} />;
            }
            if (vimeoPathAndQuery) {
              return <VideoEmbed provider="vimeo" id={vimeoPathAndQuery} />;
            }
            if (pathAndQuery) {
              return <VideoEmbed provider="youtube" id={pathAndQuery} />;
            }
            return null;
          }
        },
      },
      renderNode
    ),
  }) as ReactElement[];

  return (
    <div css={[softBreakInParagraphStyles]} {...otherProps}>
      {withArticleStyle ? wrapArticleBlocks(body) : body}
    </div>
  );
}
